import React, { useState, useEffect, useContext } from "react";

import { toast } from "react-toastify";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Stack } from "@mui/material";

// Tipos de ação disponíveis no bloco. "needs" diz quais campos o tipo usa.
export const ACTION_TYPES = [
  { value: "addTag", label: "Adicionar etiqueta", needs: "tag" },
  { value: "removeTag", label: "Remover etiqueta", needs: "tag" },
  { value: "setField", label: "Definir/Atualizar o valor de um campo", needs: "fieldValue" },
  { value: "clearField", label: "Limpar valor do campo", needs: "field" },
  { value: "openTicket", label: "Definir atendimento como aberto", needs: "" },
  { value: "assignOpen", label: "Atribuir e abrir atendimento", needs: "assignee" },
  { value: "notifyUser", label: "Notificar membro da equipe", needs: "user" },
  { value: "pauseAutomation", label: "Pausar automação (desabilitar chatbot do contato)", needs: "" },
  { value: "resumeAutomation", label: "Reiniciar automação (habilitar chatbot do contato)", needs: "" },
  { value: "unassign", label: "Remover atribuição do chat (volta para Aguardando)", needs: "" },
  { value: "closeTicket", label: "Definir atendimento como concluído", needs: "" },
];

const typeInfo = (type) => ACTION_TYPES.find((t) => t.value === type) || ACTION_TYPES[0];
const emptyRow = () => ({ type: "addTag", tagId: "", fieldName: "", value: "", userId: "", message: "" });

const FlowBuilderActionModal = ({ open, onSave, data, onUpdate, close }) => {
  const { user } = useContext(AuthContext);
  const [activeModal, setActiveModal] = useState(false);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);
  const [newTag, setNewTag] = useState({ open: false, rowIndex: 0, name: "", saving: false });

  const randomColor = () =>
    "#" +
    Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, "0");

  const handleCreateTag = async () => {
    const name = newTag.name.trim();
    if (!name) {
      return toast.error("Digite o nome da etiqueta");
    }
    setNewTag((old) => ({ ...old, saving: true }));
    try {
      const { data: created } = await api.post("/tags", {
        name,
        color: randomColor(),
        kanban: 0,
        userId: user?.id,
      });
      if (!created || !created.id) {
        throw new Error("Resposta inesperada ao criar etiqueta");
      }
      setTags((old) => [...old, created]);
      setRows((old) =>
        old.map((r, i) => (i === newTag.rowIndex ? { ...r, tagId: created.id } : r))
      );
      toast.success("Etiqueta criada");
      setNewTag({ open: false, rowIndex: 0, name: "", saving: false });
    } catch (error) {
      console.log(error);
      toast.error("Não foi possível criar a etiqueta");
      setNewTag((old) => ({ ...old, saving: false }));
    }
  };

  useEffect(() => {
    if (open !== "edit" && open !== "create") return;
    (async () => {
      try {
        const { data: tagList } = await api.get("/tags/list", {
          params: { companyId: user.companyId, kanban: 0 },
        });
        setTags(tagList || []);
        try {
          const { data: userList } = await api.get("/users/list");
          setUsers(userList || []);
        } catch (e) {
          console.log(e);
        }
        if (open === "edit" && data?.data?.actions?.length) {
          setRows(
            data.data.actions.map((a) => ({
              type: a.type,
              tagId: a.tagId || "",
              fieldName: a.fieldName || "",
              value: a.value || "",
              userId: a.userId || "",
              message: a.message || "",
            }))
          );
        } else {
          setRows([emptyRow()]);
        }
        setActiveModal(true);
      } catch (error) {
        console.log(error);
        toast.error("Não foi possível carregar as etiquetas");
      }
    })();
    // eslint-disable-next-line
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const setRow = (index, patch) =>
    setRows((old) => old.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const handleSave = () => {
    if (rows.length === 0) {
      return toast.error("Adicione ao menos uma ação");
    }
    for (const r of rows) {
      const needs = typeInfo(r.type).needs;
      if (needs === "tag" && !r.tagId) {
        return toast.error("Selecione uma etiqueta nas ações de etiqueta");
      }
      if (needs === "user" && !r.userId) {
        return toast.error("Selecione o membro da equipe na ação de notificar");
      }
      if (needs === "assignee" && !r.userId) {
        return toast.error("Selecione o membro da equipe na ação de atribuir");
      }
      if ((needs === "field" || needs === "fieldValue") && !r.fieldName.trim()) {
        return toast.error("Informe o nome do campo nas ações de campo");
      }
    }
    const actions = rows.map((r) => {
      const needs = typeInfo(r.type).needs;
      const action = { type: r.type };
      if (needs === "tag") {
        const tag = tags.find((t) => t.id === r.tagId);
        action.tagId = r.tagId;
        action.tagName = tag ? tag.name : "";
      }
      if (needs === "assignee") {
        const member = users.find((u) => u.id === r.userId);
        action.userId = r.userId;
        action.userName = member ? member.name : "";
      }
      if (needs === "user") {
        const member = users.find((u) => u.id === r.userId);
        action.userId = r.userId;
        action.userName = member ? member.name : "";
        action.message = r.message;
      }
      if (needs === "field" || needs === "fieldValue") {
        action.fieldName = r.fieldName.trim();
      }
      if (needs === "fieldValue") {
        action.value = r.value;
      }
      return action;
    });
    if (open === "edit") {
      onUpdate({ ...data, data: { actions } });
    } else {
      onSave({ actions });
    }
    handleClose();
  };

  return (
    <div>
      <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle>{open === "create" ? "Adicionar ações ao fluxo" : "Editar ações"}</DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              As ações são executadas no contato quando o fluxo chega neste bloco, na ordem em que aparecem.
            </Typography>
            {rows.map((row, index) => {
              const needs = typeInfo(row.type).needs;
              return (
                <div
                  key={index}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                    <Select
                      value={row.type}
                      fullWidth
                      onChange={(e) => setRow(index, { type: e.target.value })}
                    >
                      {ACTION_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {needs === "tag" && (
                      <Select
                        value={row.tagId}
                        displayEmpty
                        fullWidth
                        onChange={(e) => {
                          if (e.target.value === "__new__") {
                            setNewTag({ open: true, rowIndex: index, name: "", saving: false });
                            return;
                          }
                          setRow(index, { tagId: e.target.value });
                        }}
                        renderValue={(v) => {
                          const tag = tags.find((t) => t.id === v);
                          return tag ? tag.name : "Selecione a etiqueta";
                        }}
                      >
                        <MenuItem value="__new__" style={{ color: "#2563eb", fontWeight: 600 }}>
                          Adicionar nova etiqueta +
                        </MenuItem>
                        {tags.map((tag) => (
                          <MenuItem key={tag.id} value={tag.id}>
                            {tag.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    {needs === "assignee" && (
                      <Select
                        value={row.userId}
                        displayEmpty
                        fullWidth
                        onChange={(e) => setRow(index, { userId: e.target.value })}
                        renderValue={(v) => {
                          const member = users.find((u) => u.id === v);
                          return member ? member.name : "Selecione o membro da equipe";
                        }}
                      >
                        {users.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    {needs === "user" && (
                      <>
                        <Select
                          value={row.userId}
                          displayEmpty
                          fullWidth
                          onChange={(e) => setRow(index, { userId: e.target.value })}
                          renderValue={(v) => {
                            const member = users.find((u) => u.id === v);
                            return member ? member.name : "Selecione o membro da equipe";
                          }}
                        >
                          {users.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                              {u.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          label="Mensagem (use {{nome}} e {{numero}} do contato)"
                          placeholder="Ex.: Cliente {{nome}} ({{numero}}) pediu atendimento humano"
                          value={row.message}
                          onChange={(e) => setRow(index, { message: e.target.value })}
                        />
                        <Typography variant="caption" color="textSecondary">
                          O membro recebe no WhatsApp pessoal. Ele precisa ter o "Número de notificação"
                          preenchido no cadastro de usuário, e o aviso respeita o horário de trabalho e as
                          férias dele.
                        </Typography>
                      </>
                    )}
                    {(needs === "field" || needs === "fieldValue") && (
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label="Nome do campo (Informações adicionais do contato)"
                        value={row.fieldName}
                        onChange={(e) => setRow(index, { fieldName: e.target.value })}
                      />
                    )}
                    {needs === "fieldValue" && (
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label="Valor"
                        value={row.value}
                        onChange={(e) => setRow(index, { value: e.target.value })}
                      />
                    )}
                  </div>
                  <IconButton
                    size="small"
                    disabled={rows.length === 1}
                    onClick={() => setRows((old) => old.filter((_, i) => i !== index))}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </div>
              );
            })}
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setRows((old) => [...old, emptyRow()])}
            >
              + Adicionar Ação
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              {i18n.t("contactModal.buttons.cancel")}
            </Button>
            <Button color="primary" variant="contained" onClick={handleSave}>
              {open === "create" ? "Adicionar" : "Editar"}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
      <Dialog
        open={newTag.open}
        onClose={() => setNewTag((old) => ({ ...old, open: false }))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Nova etiqueta</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label="Nome da etiqueta"
            value={newTag.name}
            onChange={(e) => setNewTag((old) => ({ ...old, name: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateTag();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setNewTag((old) => ({ ...old, open: false }))}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={newTag.saving}
            onClick={handleCreateTag}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderActionModal;
